import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext();

export function useLocation() {
  return useContext(LocationContext);
}

export function LocationProvider({ children }) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [servicesDisabled, setServicesDisabled] = useState(false);


  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      // Enhanced native geolocation with better options
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Location successfully retrieved - reset disabled state
          setServicesDisabled(false);
          const { latitude, longitude } = position.coords;
          const locationData = {
            latitude,
            longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
            lastUpdated: new Date().toISOString(),
            source: 'user'
          };
          resolve(locationData);
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location services.';
              setServicesDisabled(true);
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 20000, // Increased timeout for better accuracy
          maximumAge: 0 // Do not use cached position; always fetch fresh
        }
      );
    });
  };

  // Alternative method using IP-based geolocation as fallback
  const getLocationByIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        return {
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
          accuracy: 5000, // IP-based location is less accurate
          timestamp: Date.now(),
          lastUpdated: new Date().toISOString(),
          source: 'ip'
        };
      }
      throw new Error('IP geolocation failed');
    } catch (error) {
      throw new Error('Unable to determine location');
    }
  };

  const requestLocation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Always reset disabled state when retrying - let the actual geolocation call determine it
      setServicesDisabled(false);
      
      // Check if geolocation is supported (but don't set disabled state based on Permissions API)
      if (!navigator.geolocation) {
        setServicesDisabled(true);
        setLoading(false);
        setError('Geolocation is not supported by this browser');
        return null;
      }
      
      // Try native geolocation first
      try {
        const locationData = await getCurrentLocation();
        setLocation(locationData);
        // Success - ensure disabled state is false
        setServicesDisabled(false);
        return locationData;
      } catch (nativeError) {
        // Only set disabled if it's a permission denied error
        // For other errors, don't set disabled state
        if (nativeError.message && nativeError.message.includes('denied')) {
          setServicesDisabled(true);
        }
        
        // Fallback to IP-based geolocation
        try {
          const ipLocationData = await getLocationByIP();
          setLocation(ipLocationData);
          // IP location succeeded - reset disabled state
          setServicesDisabled(false);
          return ipLocationData;
        } catch (ipError) {
          // If both methods fail, throw the original error
          throw nativeError;
        }
      }
    } catch (err) {
      console.error('Location error:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLocation = useCallback(async () => {
    return await requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    // Check if geolocation is supported when component mounts
    if (!navigator.geolocation) {
      setServicesDisabled(true);
    }
    
    // Automatically request location when the component mounts
    requestLocation();
  }, [requestLocation]);

  const value = {
    location,
    loading,
    error,
    servicesDisabled,
    requestLocation,
    updateLocation,
    getCurrentLocation,
    getLocationByIP
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}
